import $ from 'gridstrap/lib/gs-zepto';
import { GSFormField } from './form-field';
import Formatter = require('formatter.js');
import formatterDefinitions from './formatter-definitions';
import validationTests from './validation-tests';
import * as locale from 'gridstrap/lib/gs-locale';
import { suggestEmail } from '../../../../../src/lib/email-suggestion';

export class GSFormFieldInput extends GSFormField {

	//
	// Properties
	//

	private initialValue;
	private formatter;
	private tests;
	private phoneVerdict;
	private regexMessage;
	private suggestion;

	get isValid()          { return !this.root.hasClass('is-invalid'); }
	get isDirty()          { return this.value !== this.initialValue; }
	get type()             { return this.input.prop('type') || 'text'; }
	get value()            { return this.input.val().trim(); }
	get formatterPattern() { return this.input.data('gsFormFieldPattern'); }
	get validationRegex()  { return this.input.data('gsFormFieldValidationRegex'); }
	get isPhone()          { return this.type == 'tel'; }
	get isDigitsOnly()     { return this.input.is('[data-gs-form-field-digits]'); }

	set isValid(value)         { this.root.toggleClass('is-invalid', !value); }
	set validationRegex(value) { this.input.data('gsFormFieldValidationRegex', value); }

	set value(value) {
		if (this.type == 'email') value = value.toLowerCase();
		if (this.type == 'url') value = value.toLowerCase();
		this.input.val(value.trim());
	}

	//
	// Formatters
	//

	private setFormatter() {
		if (!this.formatterPattern) return;
		const definition = this.getFormatterDefinition(this.formatterPattern);
		const { pattern, patterns, validationRegex } = definition;
		this.validationRegex = validationRegex;

		// If we have an existing formatter, but the current locale
		// has no defined patterns, destroy the formatter
		if (this.formatter && !pattern && !patterns) {
			this.formatter.destroy();
		}

		// If we don't have a formatter yet, we'll create one
		else if (!this.formatter) {
			this.formatter = new Formatter(this.input.get(0), {
				persistent: false, pattern, patterns
			});
		}

		// Otherwise, reset the patterns and force a reformat
		// by triggering the keypress event
		else {
			this.formatter.resetPattern(pattern || patterns);
			if (this.value != '') this.input.trigger('keypress');
		}
	}

	private getFormatterDefinition(pattern) {
		const defs = formatterDefinitions[pattern];
		if (!defs) return { pattern };

		const country = locale.get();
		const match = defs.find(def => (def.countries || []).includes(country));
		return match || defs.find(def => def.default);
	}

	// The formatter only sees keystrokes and pastes; autofill and drag-drop
	// bypass it, and a full phone number typed into the Extension box once
	// overflowed its column and lost the lead (SC-617, API-1FX).
	private stripNonDigits() {
		if (!this.isDigitsOnly) return;
		const raw = this.input.val();
		const digits = raw.replace(/\D/g, '');
		if (digits != raw) this.input.val(digits);
	}

	//
	// Validation
	//

	private setValidationTests() {
		this.tests = [
			...validationTests.filter(test => this.root.hasClass(test.checkClass)),
			{ invalidClass: 'is-invalid-regex', test: value => this.testRegex(value) }
		];
	}

	validate(silent = false) {
		let failedTest = this.tests
			.find(test => !test.test(this.value));

		if (failedTest && failedTest.invalidClass == 'is-invalid-regex') {
			this.showRegexMessage(this.currentPhoneVerdict());
		}

		if (failedTest && !silent) {
			this.isValid = false;
			this.root.addClass(failedTest.invalidClass);
			this.input.on('keyup.validate', () => this.revalidate(failedTest));
		}

		return !failedTest;
	}

	private revalidate(failedTest) {
		if (failedTest.test(this.value)) {
			this.isValid = true;
			this.root.removeClass(failedTest.invalidClass);
			this.input.off('keyup.validate');
		}
	}

	private testRegex(value) {
		// A phone number the worker has judged is valid or not on that
		// verdict alone; the country mask's regex only decides without one.
		const verdict = this.currentPhoneVerdict();
		if (verdict) return verdict.accept;
		if (!this.validationRegex) return true;
		let pattern = new RegExp(this.validationRegex);
		return pattern.test(value);
	}

	//
	// Phone verdict (SC-616, ADR-0005 amendment)
	//

	// Ask the worker what the submit would make of this number, and keep the
	// answer for exactly the value, country and extension it judged. Resolves
	// either way: an unreachable endpoint leaves the regex in charge, as before.
	checkPhone() {
		if (!this.isPhone || !this.value) return Promise.resolve();
		if (this.currentPhoneVerdict()) return Promise.resolve();

		const fields = this.phoneFields();
		const key = this.phoneKey();
		const body = JSON.stringify({ ...fields, phoneExtension: fields.phoneExtension || undefined });

		return fetch('/api/phone/validate', {
			method: 'POST',
			headers: { 'content-type': 'application/json' },
			body
		})
			.then(res => res.ok ? res.json() : null)
			.then(verdict => {
				if (!verdict || typeof verdict.accept != 'boolean') return;
				if (key != this.phoneKey()) return;
				this.phoneVerdict = { key, accept: verdict.accept, message: verdict.message };
				this.applyPhoneVerdict();
				this.root.trigger('gs:phoneVerdict', [verdict]);
			})
			.catch(() => {});
	}

	// The three fields the submit folds into one tel URI.
	private phoneFields() {
		const form = this.root.closest('form');
		return {
			phone: this.value,
			phoneCountryCode: form.find('[name="phoneCountryCode"]').val() || locale.get(),
			phoneExtension: String(form.find('[name="phoneExtension"]').val() || '').trim()
		};
	}

	private phoneKey() {
		const { phone, phoneCountryCode, phoneExtension } = this.phoneFields();
		return [phone, phoneCountryCode, phoneExtension].join('|');
	}

	private currentPhoneVerdict() {
		if (!this.phoneVerdict || this.phoneVerdict.key != this.phoneKey()) return null;
		return this.phoneVerdict;
	}

	// Show a verdict now rather than waiting for submit: a refusal marks the
	// field invalid with the worker's reason, an acceptance clears a regex
	// failure the mask would otherwise have raised.
	private applyPhoneVerdict() {
		const verdict = this.currentPhoneVerdict();
		if (!verdict) return;
		const regexTest = this.tests.find(test => test.invalidClass == 'is-invalid-regex');

		this.showRegexMessage(verdict);
		if (verdict.accept) {
			if (!this.root.hasClass('is-invalid-regex')) return;
			this.revalidate(regexTest);
			return;
		}

		this.isValid = false;
		this.root.addClass('is-invalid-regex');
		this.input.off('keyup.validate').on('keyup.validate', () => this.revalidate(regexTest));
	}

	// The field's regex error slot carries the worker's reason while a
	// refusal is current, and its own template text otherwise.
	private showRegexMessage(verdict) {
		const slot = this.root.find('.gs-FormField-errorMessage--regex');
		if (slot.length == 0) return;
		if (this.regexMessage == null) this.regexMessage = slot.text();
		const refused = verdict && !verdict.accept && verdict.message;
		slot.text(refused ? verdict.message : this.regexMessage);
	}

	//
	// Email suggestion
	//

	// A misspelled webmail domain ("gamil.com") gets a "Did you mean" line
	// under the field on blur, the same hint the Astro lead form shows
	// (src/lib/email-suggestion.ts). The visitor may click it or ignore it;
	// the submit never waits on it.
	private showSuggestion() {
		if (this.type != 'email') return;
		const suggestion = suggestEmail(this.value);
		// Clicking the hint blurs the input first; rebuilding the hint then
		// would drop the click.
		if (suggestion == this.suggestion) return;
		this.clearSuggestion();
		if (!suggestion) return;

		// Text nodes, not markup: the mailbox is whatever the visitor typed.
		const at = suggestion.lastIndexOf('@');
		const link = $('<button type="button" class="gs-FormField-suggestionLink"></button>')
			.append(document.createTextNode(suggestion.slice(0, at + 1)))
			.append($('<strong></strong>').text(suggestion.slice(at + 1)))
			.on('click', () => this.acceptSuggestion());

		$('<div class="gs-FormField-suggestion"></div>')
			.append(document.createTextNode('Did you mean '))
			.append(link)
			.append(document.createTextNode('?'))
			.appendTo(this.root.find('.gs-FormField-wrapper'));

		this.suggestion = suggestion;
	}

	private acceptSuggestion() {
		this.value = this.suggestion;
		this.isPopulated = true;
		this.clearSuggestion();
		// Clears an invalid-email mark the old value earned (see revalidate).
		this.input.trigger('keyup');
	}

	private clearSuggestion() {
		this.suggestion = null;
		this.root.find('.gs-FormField-suggestion').remove();
	}

	//
	// Initializations & Event Listeners
	//

	constructor(root) {
		super(root);

		this.input.on({
			focus: () => this.setFormatter(),
			input: () => {
				this.stripNonDigits();
				this.clearSuggestion();
			},
			change: () => this.input.trigger('keypress'),
			blur: () => {
				let isValid = this.validate(true);
				if (isValid && this.value) this.track('valid');
				if (!isValid && this.value) this.track('invalid');
				if (this.isPhone) this.checkPhone();
				this.showSuggestion();
			}
		});

		this.setValidationTests();
		this.initialValue = this.value;
	}

	gsOnDestroy() {
		this.input.off();
		if (this.formatter) this.formatter.destroy();
	}
}
